import { fmtMoney } from "@/lib/api";

// Scoring: score = budget_remaining + diagnosis bonus
// correct = +5000, partial = +2000, incorrect/unmarked = 0
export const BONUS = { correct: 5000, partial: 2000, incorrect: 0 };

export const scoreTeam = (team) => {
  const bonus = BONUS[team.diagnosis_grade] || 0;
  return {
    budget: team.budget_remaining,
    bonus,
    total: team.budget_remaining + bonus,
  };
};

export const gradeLabel = (g) =>
  g === "correct" ? "CORRECT" : g === "partial" ? "PARTIAL" : g === "incorrect" ? "INCORRECT" : "UNGRADED";

export const gradeTone = (g) =>
  g === "correct"
    ? "var(--dxc-volt)"
    : g === "partial"
      ? "#facc15"
      : g === "incorrect"
        ? "var(--dxc-signal)"
        : "rgba(255,255,255,0.4)";

export default function Leaderboard({ teams, startingBudget, testId = "leaderboard" }) {
  const ranked = teams
    .map((t) => ({ team: t, ...scoreTeam(t) }))
    .sort((a, b) => b.total - a.total);

  const maxTotal = Math.max(1, ...ranked.map((r) => r.total));

  return (
    <div className="dxc-card p-6 md:p-8" data-testid={testId}>
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-6">
        <div>
          <div className="font-mono-data text-[10px] tracking-[0.3em] text-[color:var(--dxc-volt)] uppercase">
            Cost-Efficiency Leaderboard
          </div>
          <h2 className="font-serif-display text-4xl mt-1">Final standings</h2>
          <p className="font-mono-data text-[10px] tracking-widest text-white/40 uppercase mt-1">
            Score = Budget Saved + Diagnosis Bonus (correct +$5,000 · partial +$2,000)
          </p>
        </div>
      </div>

      {ranked.length === 0 ? (
        <div className="text-white/40 font-mono-data text-xs uppercase tracking-widest py-6">
          No teams yet.
        </div>
      ) : (
        <div className="space-y-0 border-t border-white/10">
          {ranked.map((r, i) => {
            const pct = (r.total / maxTotal) * 100;
            const tone = gradeTone(r.team.diagnosis_grade);
            const spent = startingBudget - r.team.budget_remaining;
            return (
              <div
                key={r.team.id}
                data-testid={`leaderboard-row-${r.team.id}`}
                className="grid grid-cols-12 gap-4 items-center py-4 border-b border-white/10"
              >
                <div className="col-span-1 font-mono-data text-2xl text-white/70">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="col-span-3">
                  <div className="font-serif-display text-xl leading-tight truncate">
                    {r.team.name}
                  </div>
                  <div className="font-mono-data text-[10px] text-white/40 uppercase tracking-widest mt-0.5">
                    {r.team.team_code} · Spent {fmtMoney(spent)}
                  </div>
                </div>
                <div className="col-span-2">
                  <div className="font-mono-data text-[10px] tracking-widest text-white/40 uppercase">
                    Budget
                  </div>
                  <div className="font-mono-data text-lg text-white">
                    {fmtMoney(r.budget)}
                  </div>
                </div>
                <div className="col-span-2">
                  <div className="font-mono-data text-[10px] tracking-widest text-white/40 uppercase">
                    Bonus
                  </div>
                  <div className="font-mono-data text-lg" style={{ color: tone }}>
                    +{fmtMoney(r.bonus)}
                  </div>
                  <div
                    className="font-mono-data text-[9px] tracking-widest uppercase mt-0.5"
                    style={{ color: tone }}
                  >
                    {gradeLabel(r.team.diagnosis_grade)}
                  </div>
                </div>
                <div className="col-span-4">
                  <div className="flex justify-between items-baseline">
                    <div className="font-mono-data text-[10px] tracking-widest text-white/40 uppercase">
                      Score
                    </div>
                    <div
                      className="font-mono-data text-2xl"
                      style={{ color: i === 0 ? "var(--dxc-volt)" : "#fff" }}
                    >
                      {fmtMoney(r.total)}
                    </div>
                  </div>
                  <div className="mt-2 h-1 bg-white/10 relative">
                    <div
                      className="absolute top-0 left-0 h-full"
                      style={{
                        width: `${pct}%`,
                        background: i === 0 ? "var(--dxc-volt)" : "#fff",
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
